import type {PartnerKioskUpdate_UserSessionUpdateData} from '../../generated/partner_service_pb';
import {
  AutoPlayBalanceStatus as BaySessionAutoPlayBalanceStatus, PlayerReadiness, type PlayerState
} from '../../generated/bay_session_shared_pb';
import {playerStateToUserSessionData} from '../utils/playerStateToUserSessionData';
import {
  projectRotationFromPlayerState, roundRobinStatusEquals, type RoundRobinStatus
} from '../utils/roundRobinProjection';

export type BayPlayerProjectionStatus = 'idle' | 'restoring' | 'ready' | 'error';

type BayPlayerProjectionValues = {
  userSessionData: PartnerKioskUpdate_UserSessionUpdateData | undefined, roundRobinStatus: RoundRobinStatus | null,
  confirmationBypassEnabled: boolean, confirmBeforeEachTurnEnabled: boolean, stationId: number, activeBetId: string,
  activeBetCurrency: string, activeBetAmountMinor: number, shotArmedBetId: string,
  autoPlayBalanceStatus: BaySessionAutoPlayBalanceStatus,
  // Server-derived readiness (sessionsnap.Build). CONFIRM means a readiness
  // gate is engaged: timeout_locked, GUARDIAN_REQUIRED, or the opted-in
  // walkaway lock. ConfirmReadyToSwing clears the self-clearable gates but
  // preserves GUARDIAN_REQUIRED; RespondToConfirmation clears only the
  // per-turn gate.
  readiness: PlayerReadiness
};

export type BayPlayerProjectionSnapshot = BayPlayerProjectionValues
  & { userSessionData: PartnerKioskUpdate_UserSessionUpdateData };

export type BayPlayerProjectionState = BayPlayerProjectionValues & { status: BayPlayerProjectionStatus };

export type BayPlayerProjectionAction = | { type: 'apply-snapshot'; snapshot: BayPlayerProjectionSnapshot }
  | { type: 'reset'; status: Extract<BayPlayerProjectionStatus, 'idle' | 'restoring'> } | { type: 'mark-error' }
  | { type: 'patch'; patch: Partial<BayPlayerProjectionState> };

export const createInitialBayPlayerProjection = (status: Extract<BayPlayerProjectionStatus, 'idle' | 'restoring'> = 'idle'): BayPlayerProjectionState => ({
  status, userSessionData: undefined, roundRobinStatus: null, confirmationBypassEnabled: false,
  confirmBeforeEachTurnEnabled: false, stationId: 0, activeBetId: '', activeBetCurrency: '', activeBetAmountMinor: 0,
  shotArmedBetId: '', autoPlayBalanceStatus: BaySessionAutoPlayBalanceStatus.AUTO_PLAY_BALANCE_OK,
  readiness: PlayerReadiness.UNSPECIFIED
});

/**
 * Value equality for the canonical session view. PlayerState arrives at a
 * sub-second cadence and both protobuf adapters allocate fresh objects.
 */
export function userSessionDataEquals(a: PartnerKioskUpdate_UserSessionUpdateData | undefined, b: PartnerKioskUpdate_UserSessionUpdateData | undefined): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;

  return a.sessionId === b.sessionId && a.userSessionState === b.userSessionState && a.sessionCurrentActivePlayerUsername === b.sessionCurrentActivePlayerUsername && a.isSessionFull === b.isSessionFull && a.accountSecurityBypassEnabled === b.accountSecurityBypassEnabled && a.targetDistanceYards === b.targetDistanceYards && a.previewedTargetDistanceYards === b.previewedTargetDistanceYards && a.previewedNextTargetYards === b.previewedNextTargetYards && a.previousTargetDistanceYards === b.previousTargetDistanceYards && a.previousTargetOutcome === b.previousTargetOutcome && a.hasAcceptedBet === b.hasAcceptedBet && a.playMode === b.playMode;
}

function bayPlayerProjectionEquals(a: BayPlayerProjectionValues | undefined, b: BayPlayerProjectionValues | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;

  return userSessionDataEquals(a.userSessionData, b.userSessionData) && roundRobinStatusEquals(a.roundRobinStatus, b.roundRobinStatus) && a.confirmationBypassEnabled === b.confirmationBypassEnabled && a.confirmBeforeEachTurnEnabled === b.confirmBeforeEachTurnEnabled && a.stationId === b.stationId && a.activeBetId === b.activeBetId && a.activeBetCurrency === b.activeBetCurrency && a.activeBetAmountMinor === b.activeBetAmountMinor && a.shotArmedBetId === b.shotArmedBetId && a.autoPlayBalanceStatus === b.autoPlayBalanceStatus && a.readiness === b.readiness;
}

/**
 * Converts one authoritative PlayerState into the complete Context projection.
 * The caller applies this object as one reducer action, so consumers never see
 * a ready station paired with stale challenge or rotation data.
 */
export function projectBayPlayerState(state: PlayerState | undefined, preferredActiveBetCurrency = ''): BayPlayerProjectionSnapshot | null {
  const userSessionData = playerStateToUserSessionData(state);
  if (!state || !userSessionData) return null;

  const acceptedWagers = state.sessionPlayer?.wagersByCurrency?.filter((wager) => wager.betState === 'accepted') ?? [];
  const acceptedWager = acceptedWagers.find((wager) => wager.currency === preferredActiveBetCurrency) ?? (acceptedWagers.length === 1 ? acceptedWagers[0] : undefined);
  const stationId = Number(state.currentStationId);

  return {
    userSessionData, roundRobinStatus: projectRotationFromPlayerState(state),
    confirmationBypassEnabled: userSessionData.accountSecurityBypassEnabled, shotArmedBetId: state.shotArmedBetId ?? '',
    confirmBeforeEachTurnEnabled: state.sessionPlayer?.confirmation?.onTurn === true,
    stationId: Number.isFinite(stationId) ? stationId : 0, activeBetId: state.sessionPlayer?.activeBetId ?? '',
    activeBetCurrency: acceptedWager?.currency ?? '',
    // Prefer the accepted bay_session_wagers row (bay-native path), but fall
    // back to SessionPlayer.active_bet_amount: legacy FullSwing engine-accept
    // bets have NO wager row, so without the fallback this stays pinned at 0
    // and a ResizeWager never moves it — BetSlip's amount-keyed re-fetch never
    // fires and the paytable renders the stale pre-resize amount while the
    // fee updates optimistically. active_bet_amount is projected fresh from
    // smg_bet.amount on every SessionState publish, including the post-resize
    // one, for exactly these bets.
    activeBetAmountMinor: Number(acceptedWager?.amount ?? state.sessionPlayer?.activeBetAmount ?? 0),
    autoPlayBalanceStatus: state.sessionPlayer?.autoPlayBalanceStatus ?? BaySessionAutoPlayBalanceStatus.AUTO_PLAY_BALANCE_OK,
    readiness: state.sessionPlayer?.readiness ?? PlayerReadiness.UNSPECIFIED
  };
}

export function bayPlayerProjectionReducer(state: BayPlayerProjectionState, action: BayPlayerProjectionAction): BayPlayerProjectionState {
  switch (action.type) {
    case 'apply-snapshot':
      if (state.status === 'ready' && bayPlayerProjectionEquals(state, action.snapshot)) return state;
      return {...action.snapshot, status: 'ready'};
    case 'reset':
      return createInitialBayPlayerProjection(action.status);
    case 'mark-error':
      return state.status === 'error' ? state : {...state, status: 'error'};
    case 'patch': {
      const next = {...state, ...action.patch};
      return bayPlayerProjectionStateEquals(state, next) ? state : next;
    }
  }
}

function bayPlayerProjectionStateEquals(a: BayPlayerProjectionState, b: BayPlayerProjectionState): boolean {
  return a.status === b.status && bayPlayerProjectionEquals(a, b);
}
